// The general statement reader: any bank's PDF statement, with no bank-specific code. The text items of the
// PDF (page, x, y) in; rows out only when the statement's own numbers prove them. Plain code, no AI, and it runs
// wherever the bank parsers run (the phone, the web, the landing converter), so the file never leaves the device.
//
// Pipeline: words with x/y per page → visual lines → repeated headers/footers dropped → the transaction table
// found from its header words (Date / Narration / Debit / Credit / Balance), or from how its numbers line up when
// there is no header → rows (a date starts a row, wrapped text joins it, rows stitch across pages) → checks.
// Checks, all of which must pass: the running balance on every row; the printed opening balance before the first
// row and the printed closing balance after the last; printed debit/credit totals and counts; dates inside the
// printed period. A card statement without a balance column: previous due + purchases - payments = total due.
// Anything that doesn't add up is a miss ('math'), never a silent import.
import type { ParsedStatement, ParsedTransaction } from "@paisa/types"
import type { PdfItem } from "../pdf-items"

export type GeneralMiss = "no-table" | "math" | "scanned" | "password"

export type GeneralRead =
  | { ok: true; rows: ParsedTransaction[]; statement: ParsedStatement }
  | { ok: false; reason: GeneralMiss }

// ── Words and lines ──────────────────────────────────────────────────────

export interface Word {
  page: number
  x: number
  x2: number
  y: number
  s: string
  /** Words of one PDF item (not split by a wide gap) share a cell id. */
  cell: number
}

export interface Line {
  page: number
  y: number
  words: Word[]
  text: string
}

export interface Cell {
  x: number
  x2: number
  s: string
}

/** A character's width when the PDF gives an item no width (about Helvetica at 8 pt). */
const CHAR_W = 4.4
/** Items this close in y are on one line. */
const Y_TOL = 3

export function wordsOf(items: readonly PdfItem[]): Word[] {
  const out: Word[] = []
  let cell = 0
  for (const it of items) {
    const w = it.w > 0 ? it.w : it.str.length * CHAR_W
    const cw = w / Math.max(1, it.str.length)
    cell++
    let end = -1
    for (const m of it.str.matchAll(/\S+/g)) {
      const at = m.index ?? 0
      if (end >= 0 && at - end >= 2) cell++ // two or more spaces: the item holds several cells
      out.push({
        page: it.page,
        x: it.x + at * cw,
        x2: it.x + (at + m[0].length) * cw,
        y: it.y,
        s: m[0],
        cell,
      })
      end = at + m[0].length
    }
  }
  return out
}

export function linesOf(words: Word[]): Line[] {
  const sorted = [...words].sort((a, b) => a.page - b.page || b.y - a.y || a.x - b.x)
  const lines: Line[] = []
  let cur: Line | null = null
  for (const w of sorted) {
    if (!cur || cur.page !== w.page || cur.y - w.y > Y_TOL) {
      cur = { page: w.page, y: w.y, words: [], text: "" }
      lines.push(cur)
    }
    cur.words.push(w)
  }
  for (const l of lines) {
    l.words.sort((a, b) => a.x - b.x)
    l.text = l.words.map((w) => w.s).join(" ")
  }
  return lines
}

/** Neighbouring words merged into cells: the same item, or a gap narrower than a space and a half. */
export function cellsOf(words: readonly Word[]): Cell[] {
  const out: (Cell & { id: number })[] = []
  for (const w of words) {
    const last = out[out.length - 1]
    if (last && (last.id === w.cell || w.x - last.x2 <= 3)) {
      last.s += ` ${w.s}`
      last.x2 = w.x2
      last.id = w.cell
    } else out.push({ x: w.x, x2: w.x2, s: w.s, id: w.cell })
  }
  return out.map(({ x, x2, s }) => ({ x, x2, s }))
}

// ── Amounts and dates ────────────────────────────────────────────────────

export interface Amt {
  /** Signed, in paise. */
  cents: number
  /** A Cr/Dr mark printed with the amount. */
  mark: "cr" | "dr" | null
  /** Printed negative (minus or brackets). */
  neg: boolean
}

// 1,23,456.00 · 123,456.00 · -500.00 · (500.00) · ₹500.00 · 500.00Cr · 500.00(Dr)
const AMOUNT_RE =
  /^(-)?(\()?(?:₹|rs\.?|inr)?(\d{1,3}(?:,\d{2,3})+|\d+)\.(\d{2})\)?(-)?(?:\(?(cr|dr)\)?\.?)?$/i
const MARK_RE = /^\(?(cr|dr)\)?\.?$/i
const SERIAL_RE = /^\d{1,4}\.?$/

export function parseAmt(s: string): Amt | null {
  const m = s.match(AMOUNT_RE)
  if (!m) return null
  const neg = !!m[1] || !!m[2] || !!m[5]
  const mark = (m[6]?.toLowerCase() as "cr" | "dr" | undefined) ?? null
  const abs = Number(m[3].replace(/,/g, "")) * 100 + Number(m[4])
  return { cents: neg || mark === "dr" ? -abs : abs, mark, neg }
}

export const isAmt = (s: string) => AMOUNT_RE.test(s)
const isMark = (s: string) => MARK_RE.test(s)
const isCount = (s: string) => /^\d{1,6}$/.test(s)

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"]
const MON = "(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\\.?"
const MON_NC = MON.replace("(", "(?:")
/** The date shapes Indian statements print, to find dates inside running text (period lines). */
const DATE_SRC = `(?:\\d{1,2}[/\\-.,]\\d{1,2}[/\\-.,]\\d{2,4}|\\d{1,2}[\\s/\\-]*${MON_NC}[\\s/\\-,]*\\d{2,4}|\\d{4}-\\d{2}-\\d{2}|${MON_NC}\\s+\\d{1,2},?\\s+\\d{4})`

function iso(y: number, m: number, d: number): string | null {
  const year = y < 100 ? (y > 50 ? 1900 + y : 2000 + y) : y
  if (m < 1 || m > 12 || d < 1 || d > 31 || year < 1990 || year > 2100) return null
  if (new Date(Date.UTC(year, m - 1, d)).getUTCMonth() !== m - 1) return null
  return `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
}

/** The order a statement prints numeric dates in: day first (Indian), month first, or year first. */
export type DateFormat = "dmy" | "mdy" | "ymd"

/** The order of the read in progress: a saved layout sets it for its read (reads are synchronous). */
let order: DateFormat = "dmy"

function withOrder<T>(o: DateFormat, run: () => T): T {
  const was = order
  order = o
  try {
    return run()
  } finally {
    order = was
  }
}

/** One date in a common Indian statement format → YYYY-MM-DD (null: not a date). Day before month by default. */
export function parseStatementDate(raw: string, format: DateFormat = order): string | null {
  const s = raw.trim().toLowerCase()
  let m = s.match(/^(\d{1,2})[/\-.,](\d{1,2})[/\-.,](\d{2}|\d{4})$/)
  if (m && format === "mdy") return iso(Number(m[3]), Number(m[1]), Number(m[2]))
  if (m) return iso(Number(m[3]), Number(m[2]), Number(m[1]))
  m = s.match(/^(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})$/)
  if (m) return iso(Number(m[1]), Number(m[2]), Number(m[3]))
  m = s.match(new RegExp(`^(\\d{1,2})[\\s/\\-]*${MON}[\\s/\\-,]*(\\d{2}|\\d{4})$`))
  if (m) return iso(Number(m[3]), MONTHS.indexOf(m[2]) + 1, Number(m[1]))
  m = s.match(new RegExp(`^${MON}\\s+(\\d{1,2}),?\\s+(\\d{4})$`))
  if (m) return iso(Number(m[3]), MONTHS.indexOf(m[1]) + 1, Number(m[2]))
  return null
}

/** The date the first 1-3 of these words spell (from `from`), and how many words it took. */
export function dateAt(words: readonly Word[], from = 0): { date: string; n: number } | null {
  for (let n = 3; n >= 1; n--) {
    if (from + n > words.length) continue
    const text = words
      .slice(from, from + n)
      .map((w) => w.s)
      .join(" ")
    const date = parseStatementDate(text)
    if (date) return { date, n }
  }
  return null
}

// ── Table header ─────────────────────────────────────────────────────────

type ColKind =
  | "date"
  | "valueDate"
  | "narration"
  | "ref"
  | "debit"
  | "credit"
  | "amount"
  | "type"
  | "balance"
  | "other"

interface Col extends Cell {
  kind: ColKind
}

const AMOUNT_COLS: ColKind[] = ["debit", "credit", "amount", "balance"]
const isMoney = (c: Col) => AMOUNT_COLS.includes(c.kind)

const COL_RES: [ColKind, RegExp][] = [
  ["balance", /\bbal(ance)?\b/],
  ["valueDate", /\bvalue\s*(date|dt)\b/],
  ["date", /\b(date|dt)\b/],
  ["type", /^(dr\s*\/?\s*cr|cr\s*\/?\s*dr|type)$/],
  ["debit", /withdrawal|debit|\bdr\b/],
  ["credit", /deposit|credit|\bcr\b/],
  ["ref", /chq|cheque|\bref\b|instrument|utr/],
  ["narration", /narration|particulars|description|details|remarks|merchant/],
  ["amount", /\bamount\b|\bamt\b/],
]

function kindOf(s: string): ColKind {
  const t = s
    .toLowerCase()
    .replace(/[.():₹]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
  return COL_RES.find(([, re]) => re.test(t))?.[0] ?? "other"
}

function colsOf(cells: readonly Cell[]): Col[] {
  const seen = new Set<ColKind>()
  return cells.map((c) => {
    let kind = kindOf(c.s)
    if (kind !== "other" && seen.has(kind)) kind = "other" // the first column of a kind is the one read
    seen.add(kind)
    return { ...c, kind }
  })
}

function isHeader(cols: readonly Col[]): boolean {
  const k = new Set(cols.map((c) => c.kind))
  return (
    k.has("date") && k.has("narration") && ((k.has("debit") && k.has("credit")) || k.has("amount"))
  )
}

/**
 * Header cells. Column names printed touching ("Chq/Ref No" against "Withdrawal (Dr)", or one PDF item
 * "Chq/Ref No Withdrawal (Dr)") are split before a word that names another column; "Amount" and Dr/Cr marks
 * only qualify the column they follow ("Withdrawal Amt", "Balance (Cr)").
 */
function headerCells(words: readonly Word[]): Cell[] {
  const out: Cell[] = []
  for (const c of cellsOf(words)) {
    let part: Word[] = []
    const flush = () => {
      if (!part.length) return
      out.push({ x: part[0].x, x2: part[part.length - 1].x2, s: part.map((w) => w.s).join(" ") })
      part = []
    }
    for (const w of words.filter((w) => w.x >= c.x && w.x2 <= c.x2)) {
      const k = kindOf(w.s)
      const cur = kindOf(part.map((p) => p.s).join(" "))
      if (k !== "other" && k !== "amount" && !isMark(w.s) && cur !== "other" && k !== cur) flush()
      part.push(w)
    }
    flush()
  }
  return out
}

const hasData = (l: Line) => l.words.some((w) => isAmt(w.s) || parseStatementDate(w.s))

/** Two header lines read as one ("Withdrawal" over "Amount (INR)"): cells that overlap in x are joined. */
function mergeHeader(top: Line, below: Line): Cell[] {
  const cells = headerCells(top.words)
  for (const c of headerCells(below.words)) {
    const hit = cells.find((t) => c.x < t.x2 + 4 && c.x2 > t.x - 4)
    if (hit) {
      hit.s += ` ${c.s}`
      hit.x = Math.min(hit.x, c.x)
      hit.x2 = Math.max(hit.x2, c.x2)
    } else cells.push(c)
  }
  return cells.sort((a, b) => a.x - b.x)
}

interface Header {
  at: number
  /** Lines it takes: 1, or 2 for a header printed over two lines. */
  n: number
  cols: Col[]
}

function headerAt(lines: readonly Line[], i: number): Header | null {
  const top = lines[i]
  if (hasData(top)) return null
  const one = colsOf(headerCells(top.words))
  const below = lines[i + 1]
  if (below && below.page === top.page && top.y - below.y <= 14 && !hasData(below)) {
    const two = colsOf(mergeHeader(top, below))
    const kinds = (cs: Col[]) => new Set(cs.map((c) => c.kind)).size
    if (isHeader(two) && kinds(two) >= kinds(one)) return { at: i, n: 2, cols: two }
  }
  return isHeader(one) ? { at: i, n: 1, cols: one } : null
}

// ── Repeated headers and footers ─────────────────────────────────────────

const looksLikeRow = (l: Line) => !!dateAt(l.words) && l.words.some((w) => isAmt(w.s))

/** Lines printed at the same place on most pages (letterhead, "Page 2 of 5", disclaimers) are dropped. */
export function dropRepeats(lines: Line[]): Line[] {
  const pages = new Set(lines.map((l) => l.page)).size
  if (pages < 2) return lines
  const need = Math.max(2, Math.ceil(pages / 2))
  const byKey = new Map<string, Line[]>()
  for (const l of lines) {
    const key = l.text.toLowerCase().replace(/\d+/g, "#")
    byKey.set(key, [...(byKey.get(key) ?? []), l])
  }
  // The table header repeats on purpose (it is read, not dropped): every line of it
  const header = new Set<Line>()
  const headerEnds = new Set<Line>()
  for (let i = 0; i < lines.length; i++) {
    const h = headerAt(lines, i)
    if (!h) continue
    for (let k = 0; k < h.n; k++) header.add(lines[i + k])
    headerEnds.add(lines[i + h.n - 1])
  }
  // A row's wrapped narration: right under the row, under another such line, or under the table header (a row
  // stitched across pages). The same narration can land at the same place on another page, so it can look
  // repeated.
  const wrapped = new Set<Line>()
  for (let i = 1; i < lines.length; i++) {
    const above = lines[i - 1]
    const l = lines[i]
    const head = headerEnds.has(above) // a header sits further above the table than a line wraps
    if (above.page !== l.page || above.y - l.y > (head ? 21 : 14) || hasData(l)) continue
    if (head || looksLikeRow(above) || wrapped.has(above)) wrapped.add(l)
  }
  const drop = new Set<Line>()
  for (const group of byKey.values()) {
    if (group.length < need) continue
    for (const l of group) {
      const near = group.filter((o) => Math.abs(o.y - l.y) <= Y_TOL)
      if (new Set(near.map((o) => o.page)).size < need) continue
      // rows never repeat
      if (looksLikeRow(l) || wrapped.has(l) || header.has(l)) continue
      drop.add(l)
    }
  }
  return lines.filter((l) => !drop.has(l))
}

// ── Reading a table line ─────────────────────────────────────────────────

interface LineRead {
  line: Line
  date: string | null
  values: Partial<Record<ColKind, Amt>>
  /** Narration words, left to right. */
  text: Word[]
  valueDate: string | null
  ref: Word[]
  type: Word[]
  /** Amounts printed in money columns (0.00 counts: it is printed). */
  printed: number
}

/** Interval distance between a word and a column (0 when they overlap), then centre distance. */
function nearest(w: Word, cols: readonly Col[]): Col {
  let best = cols[0]
  let bestD = Number.POSITIVE_INFINITY
  let bestC = Number.POSITIVE_INFINITY
  for (const c of cols) {
    const d = Math.max(0, c.x - w.x2, w.x - c.x2)
    const cd = Math.abs((c.x + c.x2) / 2 - (w.x + w.x2) / 2)
    if (d < bestD || (d === bestD && cd < bestC)) {
      best = c
      bestD = d
      bestC = cd
    }
  }
  return best
}

/** The column a text word sits in: the last column starting at or left of it (text is left-aligned). */
function columnOf(w: Word, cols: readonly Col[]): Col {
  let hit = cols[0]
  for (const c of cols) if (c.x - 4 <= w.x) hit = c
  return hit
}

function readLine(line: Line, cols: readonly Col[]): LineRead {
  const out: LineRead = {
    line,
    date: null,
    values: {},
    text: [],
    valueDate: null,
    ref: [],
    type: [],
    printed: 0,
  }
  const dateIdx = cols.findIndex((c) => c.kind === "date")
  const dateWords: Word[] = []
  const valueWords: Word[] = []
  const words = line.words
  for (let i = 0; i < words.length; i++) {
    const w = words[i]
    const numeric = isAmt(w.s) || w.s === "-"
    const col = numeric ? nearest(w, cols) : columnOf(w, cols)
    if (numeric && isMoney(col)) {
      let amt = parseAmt(w.s)
      const next = words[i + 1]
      if (amt && next && isMark(next.s) && next.x - w.x2 <= 8) {
        amt = parseAmt(`${w.s}${next.s.replace(/[().]/g, "")}`) // "45,000.00 Cr": the mark is the next word
        i++
      }
      if (amt) {
        out.printed++
        if (!out.values[col.kind]?.cents) out.values[col.kind] = amt
      }
      continue
    }
    const idx = cols.indexOf(col)
    if (col.kind === "date") dateWords.push(w)
    else if (col.kind === "valueDate") valueWords.push(w)
    else if (col.kind === "ref") out.ref.push(w)
    else if (col.kind === "type") out.type.push(w)
    else if (col.kind === "other") {
      // a serial number before the date; other "other" columns (branch codes) are not read
      if (idx < dateIdx && !SERIAL_RE.test(w.s)) dateWords.push(w)
    } else out.text.push(w) // narration, and text spilling into a money column
  }
  const d = dateAt(dateWords)
  if (d) {
    out.date = d.date
    // the rest of the date column is narration printed from the column's left edge
    out.text = [...dateWords.slice(d.n), ...out.text].sort((a, b) => a.x - b.x)
  } else out.text = [...dateWords, ...out.text].sort((a, b) => a.x - b.x)
  const vd = dateAt(valueWords)
  if (vd) out.valueDate = vd.date
  else if (valueWords.length) out.text = [...out.text, ...valueWords].sort((a, b) => a.x - b.x)
  // a value date printed first in the narration ("02/09/2026 02/09/2026 UPI/...")
  if (out.date && !out.valueDate) {
    const lead = dateAt(out.text)
    if (lead) {
      out.valueDate = lead.date
      out.text = out.text.slice(lead.n)
    }
  }
  return out
}

type Special = "open" | "close" | "total" | "end"

const OPEN_ROW =
  /\b(opening\s+bal(ance)?|balance\s+b\/?f|b\/f|brought\s+forward|balance\s+forward)\b/i
const CLOSE_ROW = /\b(closing\s+bal(ance)?|balance\s+c\/?f|c\/f|carried\s+forward)\b/i
const TOTAL_ROW = /^(grand\s+)?totals?\b|\btransaction\s+totals?\b/i
const END_ROW =
  /\b(statement\s+summary|account\s+summary|summary\s+of\s+(your\s+)?account|end\s+of\s+(the\s+)?statement)\b|^\*{3,}/i

function specialOf(r: LineRead): Special | null {
  const t = r.line.text
  const moves = !!(r.values.debit?.cents || r.values.credit?.cents || r.values.amount?.cents)
  if (END_ROW.test(t)) return "end"
  if (!r.date && TOTAL_ROW.test(t) && !/\bpage\b/i.test(t)) return "total"
  if (!moves && OPEN_ROW.test(t)) return "open"
  if (!moves && CLOSE_ROW.test(t)) return "close"
  return null
}

// ── Summary values (outside the table) ───────────────────────────────────

const VALUE_AMT = String.raw`-?\(?(?:₹|rs\.?|inr)?\s?(?:\d{1,3}(?:,\d{2,3})+|\d+)\.\d{2}\)?(?:\s*\(?(?:cr|dr)\)?\.?)?`
const SEP = String.raw`\s*(?:\((?:in\s+)?(?:rs\.?|inr|₹)\)\s*)?[:=\-]?\s*(?:rs\.?|inr|₹)?\s*`

interface Label {
  /** Matched in running text, followed by its value ("Opening Balance : 1,000.00"). */
  inline: string
  /** A whole summary-table cell, with its value in the cell below ("Debits" over "12,000.00"). */
  cell: RegExp
}

const L = {
  open: { inline: String.raw`opening\s+bal(?:ance)?`, cell: /^opening\s+bal(ance)?$/ },
  close: { inline: String.raw`closing\s+bal(?:ance)?`, cell: /^closing\s+bal(ance)?$/ },
  debits: {
    inline: String.raw`total\s+(?:debits?|withdrawals?)(?:\s+amount)?`,
    cell: /^(total\s+)?(debits?|withdrawals?)(\s+amount)?$/,
  },
  credits: {
    inline: String.raw`total\s+(?:credits?|deposits?)(?:\s+amount)?`,
    cell: /^(total\s+)?(credits?|deposits?)(\s+amount)?$/,
  },
  debitCount: {
    inline: String.raw`(?:(?:dr|debit|withdrawal)s?\s+count|no\.?\s+of\s+(?:debits?|withdrawals?))`,
    cell: /^((dr|debit|withdrawal)s?\s+count|no\s+of\s+(debits?|withdrawals?))$/,
  },
  creditCount: {
    inline: String.raw`(?:(?:cr|credit|deposit)s?\s+count|no\.?\s+of\s+(?:credits?|deposits?))`,
    cell: /^((cr|credit|deposit)s?\s+count|no\s+of\s+(credits?|deposits?))$/,
  },
  prevDue: {
    inline: String.raw`previous\s+(?:balance|dues?|statement\s+(?:balance|dues?))`,
    cell: /^(previous\s+(balance|dues?|statement\s+(balance|dues?))|opening\s+balance)$/,
  },
  totalDue: {
    inline: String.raw`total\s+(?:amount\s+)?dues?`,
    cell: /^(total\s+(amount\s+)?dues?|total\s+outstanding|new\s+balance)$/,
  },
  purchases: {
    inline: String.raw`total\s+purchases?`,
    cell: /^(total\s+)?(purchases?|debits?|new\s+charges|purchases?\s*(\/|&|and)\s*(other\s+)?(debits?|charges))$/,
  },
  payments: {
    inline: String.raw`total\s+payments?`,
    cell: /^(total\s+)?(payments?|credits?|payments?\s*(\/|&|and)\s*(other\s+)?credits?)$/,
  },
} satisfies Record<string, Label>

const cellText = (s: string) =>
  s
    .toLowerCase()
    .replace(/\((in\s+)?(rs\.?|inr|₹)\)|[:₹.]/g, " ")
    .replace(/\s+/g, " ")
    .trim()

/** Every value these lines print for a label: amounts in paise, or counts. */
function labelled(lines: readonly Line[], label: Label, count = false): number[] {
  const out: number[] = []
  const value = count ? String.raw`\d{1,6}(?![\d,.])` : VALUE_AMT
  const re = new RegExp(`\\b(?:${label.inline})${SEP}(${value})`, "gi")
  const read = (s: string) => (count ? Number(s) : parseAmt(s.replace(/\s+/g, ""))?.cents)
  lines.forEach((line, i) => {
    let inline = false
    for (const m of line.text.matchAll(re)) {
      const v = read(m[1])
      if (v != null) {
        out.push(v)
        inline = true
      }
    }
    if (inline) return
    for (const c of cellsOf(line.words)) {
      if (!label.cell.test(cellText(c.s))) continue
      // the value sits under the label, in one of the next two lines
      for (const below of lines.slice(i + 1, i + 3)) {
        if (below.page !== line.page || line.y - below.y > 30) break
        const centre = (c.x + c.x2) / 2
        const hit = cellsOf(below.words)
          .filter((v) => (count ? isCount(v.s) : isAmt(v.s.replace(/\s+/g, ""))))
          .map((v) => ({ v, d: Math.abs((v.x + v.x2) / 2 - centre) }))
          .filter(({ v, d }) => d <= 30 || (v.x < c.x2 && v.x2 > c.x))
          .sort((a, b) => a.d - b.d)[0]
        const v = hit ? read(hit.v.s) : undefined
        if (v != null) {
          out.push(v)
          break
        }
      }
    }
  })
  return out
}

const PERIOD_RE = new RegExp(
  `(${DATE_SRC})\\s*\\)?\\s*(?:to|till|until|and|-|–)\\s*:?\\s*(${DATE_SRC})`,
  "i",
)

function periodOf(lines: readonly Line[]): { start: string; end: string } | null {
  for (const l of lines) {
    if (!/period|from|statement|between|for/i.test(l.text)) continue
    const m = l.text.match(PERIOD_RE)
    const start = m && parseStatementDate(m[1])
    const end = m && parseStatementDate(m[2])
    if (start && end && start <= end) return { start, end }
  }
  return null
}

function accountOf(lines: readonly Line[]): string | null {
  for (const l of lines) {
    const m = l.text.match(
      /\b(?:account|a\/c|acct|card)\s*(?:no|number|num)?\.?\s*:?\s*([x*\d][x*\d\s-]{2,24}\d{4})\b/i,
    )
    if (m) return m[1].replace(/\D/g, "").slice(-4)
  }
  return null
}

// ── The reader ───────────────────────────────────────────────────────────

/** Fewer characters than this in the whole file: no text layer (a scan). */
const MIN_TEXT = 40

/** The general reader over a PDF's text items (what readPdf returns). */
export function readGeneralStatement(items: readonly PdfItem[]): GeneralRead {
  const chars = items.reduce((n, i) => n + i.str.trim().length, 0)
  if (chars < MIN_TEXT) return { ok: false, reason: "scanned" }
  const lines = dropRepeats(linesOf(wordsOf(items)))

  const headers = new Map<number, Header>()
  for (let i = 0; i < lines.length; i++) {
    if (headers.has(lines[i].page)) continue
    const h = headerAt(lines, i)
    if (h) headers.set(lines[i].page, h)
  }
  if (headers.size) return readTable(lines, headers, null)

  // No header: columns from how the numbers line up, each candidate checked by the math
  const candidates = alignedColumns(lines)
  let last: GeneralRead = { ok: false, reason: "no-table" }
  for (const cols of candidates) {
    last = readTable(lines, headers, cols)
    if (last.ok) return last
  }
  return last
}

/**
 * Columns for a statement with no header line: rows are lines that start with a date and carry amounts; the
 * amounts' right edges cluster into columns. The rightmost column on (nearly) every row is the balance. One
 * other column is a signed amount; two are debit and credit, tried both ways round (the balance math decides).
 */
function alignedColumns(lines: readonly Line[]): Col[][] {
  const rows: { l: Line; x: number; x2: number; amts: Word[] }[] = []
  for (const l of lines) {
    const skip = l.words.length > 1 && SERIAL_RE.test(l.words[0].s) ? 1 : 0
    const d = dateAt(l.words, skip) ?? (skip ? null : null)
    const amts = l.words.filter((w) => isAmt(w.s))
    if (!d || !amts.length) continue
    rows.push({ l, x: l.words[0].x, x2: l.words[skip + d.n - 1].x2, amts })
  }
  if (rows.length < 2) return []
  const clusters: { x: number; x2: number; rows: Set<Line> }[] = []
  for (const r of rows)
    for (const w of r.amts) {
      const c = clusters.find((k) => Math.abs(k.x2 - w.x2) <= 10)
      if (c) {
        c.x = Math.min(c.x, w.x)
        c.x2 = Math.max(c.x2, w.x2)
        c.rows.add(r.l)
      } else clusters.push({ x: w.x, x2: w.x2, rows: new Set([r.l]) })
    }
  clusters.sort((a, b) => a.x - b.x)
  const dateX = Math.min(...rows.map((r) => r.x))
  const dateX2 = Math.max(...rows.map((r) => r.x2))
  const col = (kind: ColKind, c: { x: number; x2: number }): Col => ({ kind, s: "", ...c })
  const base = [
    col("date", { x: dateX, x2: dateX2 }),
    col("narration", { x: dateX2 + 2, x2: dateX2 + 3 }),
  ]
  const last = clusters[clusters.length - 1]
  const hasBalance = last.rows.size >= rows.length * 0.8
  const others = hasBalance ? clusters.slice(0, -1) : clusters
  if (!hasBalance) return others.length === 1 ? [[...base, col("amount", others[0])]] : []
  if (others.length === 1) return [[...base, col("amount", others[0]), col("balance", last)]]
  if (others.length !== 2) return []
  const [a, b] = others
  return [
    [...base, col("debit", a), col("credit", b), col("balance", last)],
    [...base, col("credit", a), col("debit", b), col("balance", last)],
  ]
}

function quantile(xs: number[], q: number): number {
  if (!xs.length) return 10
  const s = [...xs].sort((a, b) => a - b)
  return s[Math.min(s.length - 1, Math.floor(q * s.length))]
}

interface RawRow {
  read: LineRead
  text: string[]
  ref: string[]
}

/**
 * Rows from the table. `aligned` is the column set for a statement with no header (then every line is a
 * candidate, and the table starts at the first dated row).
 */
function readTable(
  lines: readonly Line[],
  headers: Map<number, Header>,
  aligned: Col[] | null,
): GeneralRead {
  const first = [...headers.values()].sort((a, b) => a.at - b.at)[0]
  let cols = aligned ?? first.cols
  const card = !cols.some((c) => c.kind === "balance")

  // The table: from a header to a summary/closing/total line; a page with no header carries it on from its
  // top. Lines outside it (and specials) are where printed totals, the period and the account are read.
  const used = new Set<Line>()
  const reads: LineRead[] = []
  const special: { kind: Special; read: LineRead }[] = []
  let open = !!aligned
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const h = aligned ? undefined : headers.get(line.page)
    if (h && i === h.at) {
      cols = h.cols
      open = true
      for (let k = 0; k < h.n; k++) used.add(lines[i + k])
      i += h.n - 1
      continue
    }
    if (!open || (h && i < h.at)) continue
    const r = readLine(line, cols)
    const sp = specialOf(r)
    if (sp) {
      special.push({ kind: sp, read: r })
      if (sp === "end" || (sp === "close" && reads.some((x) => x.date))) open = false
      continue
    }
    reads.push(r)
  }

  const isRow = (r: LineRead) => !!r.date && (r.printed > 0 || !aligned)
  if (!reads.some((r) => r.date && r.printed > 0)) return { ok: false, reason: "no-table" }

  const gaps: number[] = []
  for (let i = 1; i < reads.length; i++) {
    const a = reads[i - 1].line
    const b = reads[i].line
    if (a.page === b.page && a.y - b.y >= 3) gaps.push(a.y - b.y)
  }
  const pitch = quantile(gaps, 0.25)

  // Blocks: runs of lines with no gap wider than a line and a half. When block after block holds one dated row
  // with text above it, the layout prints part of the narration above the date (SBI-like): text then goes to
  // the nearest row of its block. Otherwise wrapped text joins the row above it.
  const blocks: LineRead[][] = []
  for (let i = 0; i < reads.length; i++) {
    const prev = reads[i - 1]
    const r = reads[i]
    if (!prev || prev.line.page !== r.line.page || prev.line.y - r.line.y > pitch * 1.6)
      blocks.push([r])
    else blocks[blocks.length - 1].push(r)
  }
  const withRows = blocks.filter((b) => b.some(isRow))
  const single = withRows.filter((b) => b.filter(isRow).length === 1)
  const above = single.filter((b) => !isRow(b[0]))
  const labelAbove =
    single.length >= withRows.length * 0.8 &&
    above.length > 0 &&
    above.length >= single.length * 0.5

  // Wrapped text lies between the table's left edge and its first money column
  const left = Math.min(...cols.map((c) => c.x)) - 6
  const moneyX = reads
    .filter(isRow)
    .flatMap((r) => r.line.words.filter((w) => isAmt(w.s) && isMoney(nearest(w, cols))))
    .map((w) => w.x)
  const right = Math.min(...moneyX, ...cols.filter(isMoney).map((c) => c.x)) + (aligned ? 0 : 6)
  const textShaped = (r: LineRead) =>
    r.printed === 0 && !r.date && r.line.words.every((w) => w.x >= left && w.x2 <= right)

  const rows: RawRow[] = []
  const owner = new Map<LineRead, RawRow>()
  let orphan = false
  const begin = (r: LineRead) => {
    const raw: RawRow = { read: r, text: [], ref: [] }
    rows.push(raw)
    used.add(r.line)
    return raw
  }
  const join = (raw: RawRow, r: LineRead) => {
    owner.set(r, raw)
    used.add(r.line)
  }

  if (labelAbove) {
    for (const b of blocks) {
      const own = b.filter(isRow)
      if (!own.length) continue
      for (const r of own) owner.set(r, begin(r))
      for (const r of b) {
        if (isRow(r)) continue
        if (r.printed > 0) orphan = true
        else if (textShaped(r)) {
          // the dated row nearest in y (a tie goes to the row above)
          const to = own.reduce((best, o) =>
            Math.abs(o.line.y - r.line.y) < Math.abs(best.line.y - r.line.y) ? o : best,
          )
          const raw = owner.get(to)
          if (raw) join(raw, r)
        }
      }
    }
  } else {
    let cur: RawRow | null = null
    let prev: Line | null = null
    for (const r of reads) {
      if (isRow(r)) {
        cur = begin(r)
        owner.set(r, cur)
      } else if (!rows.length) {
        // before the first row: not the table yet
      } else if (r.printed > 0) orphan = true
      else if (cur && textShaped(r)) {
        // wrapped text: right under the row, or at the top of the next page (a row stitched across pages)
        if (!prev || prev.page !== r.line.page || prev.y - r.line.y <= pitch * 2.5) join(cur, r)
        else cur = null // a gap: the table has ended on this page
      }
      prev = r.line
    }
  }
  if (orphan) return { ok: false, reason: "math" } // amounts with no row (a row split from its amounts)

  // each row's text in reading order: the lines it owns, top to bottom
  for (const r of reads) {
    const raw = owner.get(r)
    if (!raw) continue
    raw.text.push(...r.text.map((w) => w.s))
    raw.ref.push(...r.ref.map((w) => w.s))
  }
  const outside = lines.filter((l) => !used.has(l))
  return verify(rows, outside, special, card)
}

// ── Checks ───────────────────────────────────────────────────────────────

const clean = (parts: string[]) =>
  parts
    .join(" ")
    .replace(/\s{2,}/g, " ")
    .trim()

/** The balance an opening/closing row prints (its balance column, else its only amount). */
function balanceOf(r: LineRead): number[] {
  const v = r.values.balance ?? r.values.amount ?? r.values.credit ?? r.values.debit
  return v ? [v.cents] : []
}

/** A row as the checks see it: from the table (verify) or from an AI read (verifyRows). */
interface Row {
  date: string
  /** Paise, unsigned. */
  cents: number
  type: "debit" | "credit" | null
  balance: number | null
  text: string[]
  ref: string[]
  valueDate: string | null
}

function verify(
  raws: RawRow[],
  outside: Line[],
  special: { kind: Special; read: LineRead }[],
  card: boolean,
): GeneralRead {
  const miss: GeneralRead = { ok: false, reason: "math" }
  const rows: Row[] = []
  for (const raw of raws) {
    const v = raw.read.values
    const d = v.debit?.cents ?? 0
    const c = v.credit?.cents ?? 0
    if (d && c) return miss // money both ways on one row: the columns were misread
    let type: Row["type"] = null
    let cents = 0
    if (d) {
      type = "debit"
      cents = Math.abs(d)
    } else if (c) {
      type = "credit"
      cents = Math.abs(c)
    } else if (v.amount?.cents) {
      const a = v.amount
      cents = Math.abs(a.cents)
      const t = raw.read.type.map((w) => w.s.toLowerCase()).join(" ")
      if (a.mark === "cr" || /\bcr\b|credit/.test(t)) type = "credit"
      else if (a.mark === "dr" || /\bdr\b|debit/.test(t)) type = "debit"
      else if (a.neg) type = card ? "credit" : "debit"
      else if (card) type = "debit"
    } else if (!(v.debit || v.credit || v.amount)) return miss // no amount at all
    if (!card && !v.balance) return miss
    rows.push({
      date: raw.read.date ?? "",
      cents,
      type,
      balance: v.balance?.cents ?? null,
      text: raw.text,
      ref: raw.ref,
      valueDate: raw.read.valueDate,
    })
  }
  return check(rows, outside, special, card)
}

/**
 * The checks every read must pass: the running balance on every row, the printed opening and closing balances,
 * printed totals and counts, dates inside the printed period (a card: previous due + purchases - payments).
 */
function check(
  rows: Row[],
  outside: Line[],
  special: { kind: Special; read: LineRead }[],
  card: boolean,
): GeneralRead {
  const miss: GeneralRead = { ok: false, reason: "math" }
  const of = (kind: Special) => special.filter((s) => s.kind === kind).map((s) => s.read)
  const opens = [...labelled(outside, card ? L.prevDue : L.open), ...of("open").flatMap(balanceOf)]
  const closes = [
    ...labelled(outside, card ? L.totalDue : L.close),
    ...of("close").flatMap(balanceOf),
  ]
  const totalRows = of("total").map((r) => r.values)
  const debitTotals = [
    ...labelled(outside, card ? L.purchases : L.debits),
    ...totalRows.flatMap((v) => (v.debit ? [Math.abs(v.debit.cents)] : [])),
  ]
  const creditTotals = [
    ...labelled(outside, card ? L.payments : L.credits),
    ...totalRows.flatMap((v) => (v.credit ? [Math.abs(v.credit.cents)] : [])),
  ]
  if (opens.some((o) => o !== opens[0])) return miss

  if (card) {
    if (!opens.length || !closes.length) return miss // nothing printed to prove the rows against
  } else {
    // the running balance on every row, from the printed opening balance when there is one
    let prev: number | null = opens.length ? opens[0] : null
    if (prev === null && rows.length < 2) return miss // one row and no opening balance: nothing proves it
    for (const r of rows) {
      const bal = r.balance ?? 0
      if (prev !== null) {
        const delta = bal - prev
        if (r.cents === 0 && delta !== 0) return miss
        if (!r.type && r.cents) {
          // a signless amount: the balance says which way it went
          if (Math.abs(delta) !== r.cents) return miss
          r.type = delta > 0 ? "credit" : "debit"
        } else if (r.cents && delta !== (r.type === "credit" ? r.cents : -r.cents)) return miss
      } else if (!r.type && r.cents) return miss // a signless first row needs the opening balance
      prev = bal
    }
    if (closes.some((c) => c !== prev)) return miss
  }

  const moving = rows.filter((r) => r.cents > 0)
  const sum = (t: "debit" | "credit") =>
    moving.filter((r) => r.type === t).reduce((n, r) => n + r.cents, 0)
  const count = (t: "debit" | "credit") => moving.filter((r) => r.type === t).length
  if (card && closes.some((c) => c !== opens[0] + sum("debit") - sum("credit"))) return miss
  if (debitTotals.some((t) => t !== sum("debit"))) return miss
  if (creditTotals.some((t) => t !== sum("credit"))) return miss
  if (labelled(outside, L.debitCount, true).some((n) => n !== count("debit"))) return miss
  if (labelled(outside, L.creditCount, true).some((n) => n !== count("credit"))) return miss

  const period = periodOf(outside)
  const dateOf = (r: Row) => r.date
  if (period && rows.some((r) => dateOf(r) < period.start || dateOf(r) > period.end)) return miss
  if (!moving.length) return { ok: false, reason: "no-table" }

  const valueDtRe = new RegExp(`\\s*value\\s*(?:dt|date)\\s*:?\\s*(${DATE_SRC})`, "i")
  const transactions: ParsedTransaction[] = moving.map((r) => {
    let description = clean(r.text)
    let valueDate = r.valueDate
    const vd = description.match(valueDtRe)
    if (vd) {
      // "UPI-... Value Dt 01/07/2026": the value date, lifted out of the narration
      valueDate = valueDate ?? parseStatementDate(vd[1])
      description = clean([description.replace(vd[0], " ")])
    }
    return {
      date: dateOf(r),
      description,
      amount: r.cents / 100,
      type: r.type ?? "debit",
      referenceNumber: clean(r.ref) || null,
      valueDate,
      closingBalance: card || r.balance === null ? null : r.balance / 100,
      paymentMethod: null,
      extractedName: null,
      upiId: null,
      sourceCategory: null,
    }
  })
  const dates = transactions.map((t) => t.date).sort()
  const text = outside.map((l) => l.text).join(" ")
  return {
    ok: true,
    rows: transactions,
    statement: {
      bank: "",
      accountType: card
        ? "credit_card"
        : /\bcurrent\s+(a\/?c|account)\b/i.test(text)
          ? "current"
          : "savings",
      accountNumber: accountOf(outside),
      periodStart: period?.start ?? dates[0],
      periodEnd: period?.end ?? dates[dates.length - 1],
      transactions,
    },
  }
}

// ── Rows read elsewhere ──────────────────────────────────────────────────

export interface BalanceCheck {
  verified: boolean
  /** Rupees the rows' running balance ends away from the printed closing (+ = above it); 0 when verified. */
  offBy: number
}

/**
 * The balance math verify runs, for rows that didn't come from PDF items (a server read of statement text):
 * opening ± every row = closing, and each row's own printed balance on the way. A card statement runs the
 * other way round (previous due + purchases − payments = total due). Whole paise, so no float drift.
 */
export function checkBalance(
  rows: readonly Pick<ParsedTransaction, "amount" | "type" | "closingBalance">[],
  opening: number,
  closing: number,
  card = false,
): BalanceCheck {
  const paise = (n: number) => Math.round(n * 100)
  let bal = paise(opening)
  let everyRow = true
  for (const r of rows) {
    const up = (r.type === "credit") !== card
    bal += up ? paise(r.amount) : -paise(r.amount)
    if (r.closingBalance !== null && paise(r.closingBalance) !== bal) everyRow = false
  }
  const off = bal - paise(closing)
  return { verified: everyRow && off === 0, offBy: off / 100 }
}

// ── Layouts and AI reads ─────────────────────────────────────────────────
// When the reader misses, the person can ask AI to read one statement (opt-in, per statement). Nothing AI returns
// is trusted: its rows go through the same checks (verifyRows), and the table layout it names is read again by
// this code (readWithLayout). Only a layout that reads the statement by code is kept (on the phone), so the next
// statement from that bank is read here with no AI. A layout holds no statement content.

/** The columns a layout can name: the reader's own column kinds. */
export const LAYOUT_COLUMNS = [
  "date",
  "valueDate",
  "narration",
  "ref",
  "debit",
  "credit",
  "amount",
  "type",
  "balance",
] as const

export type LayoutColumn = (typeof LAYOUT_COLUMNS)[number]

/** A bank's statement table: its columns' x-ranges, its header words and its date order. */
export interface StatementLayout {
  /** The bank's name: a label for the saved layout. */
  bank: string
  headerWords: string[]
  dateFormat: DateFormat
  columns: { kind: LayoutColumn; x: number; x2: number }[]
}

const tokens = (s: string) => s.toLowerCase().split(/\s+/).filter(Boolean)

/** Every header word of the layout is printed in this statement (a statement from its bank). */
export function layoutFits(items: readonly PdfItem[], layout: StatementLayout): boolean {
  const words = new Set(items.flatMap((i) => tokens(i.str)))
  return layout.headerWords.flatMap(tokens).every((w) => words.has(w))
}

/** Only column titles ("Txn Date", "Withdrawals") may be kept in a layout: never a name, number or narration. */
export function layoutWords(words: readonly string[]): string[] {
  const titles = words
    .map((w) => w.trim().replace(/\s+/g, " "))
    .filter((w) => w && w.length <= 30 && !/\d|@/.test(w) && kindOf(w) !== "other")
  return [...new Set(titles)].slice(0, 12)
}

/** The statement read by this code with a layout (saved, or named by AI), through the same checks. */
export function readWithLayout(items: readonly PdfItem[], layout: StatementLayout): GeneralRead {
  const cols: Col[] = layout.columns
    .map((c) => ({ kind: c.kind, x: Math.min(c.x, c.x2), x2: Math.max(c.x, c.x2), s: "" }))
    .sort((a, b) => a.x - b.x)
  if (!cols.some((c) => c.kind === "date") || !cols.some(isMoney) || !layoutFits(items, layout))
    return { ok: false, reason: "no-table" }
  const chars = items.reduce((n, i) => n + i.str.trim().length, 0)
  if (chars < MIN_TEXT) return { ok: false, reason: "scanned" }
  return withOrder(layout.dateFormat, () =>
    readTable(dropRepeats(linesOf(wordsOf(items))), new Map(), cols),
  )
}

/** A row as AI returns it: rupees, the date as YYYY-MM-DD, the balance after the row (null on a card). */
export interface AiRow {
  date: string
  description: string
  debit: number | null
  credit: number | null
  balance: number | null
}

/**
 * AI's rows through the reader's checks. Every amount and balance must be printed in the statement, and the
 * opening/closing balances, totals, counts and period are read from the statement by this code, never from AI.
 * Each row's date must also be printed in the statement, read in ONE order (`format`: day first unless the
 * layout says otherwise), so a wrong day or a swapped day and month is refused. Month first is accepted only
 * when the statement proves it: some printed date reads that way and cannot be read day first.
 */
export function verifyRows(
  items: readonly PdfItem[],
  rows: readonly AiRow[],
  card: boolean,
  format: DateFormat = "dmy",
): GeneralRead {
  const miss: GeneralRead = { ok: false, reason: "math" }
  const lines = dropRepeats(linesOf(wordsOf(items)))
  const printed = new Set<number>()
  for (const l of lines)
    for (const w of l.words) {
      const a = parseAmt(w.s)
      if (a) printed.add(Math.abs(a.cents))
    }
  const dates = new Set<string>()
  let proven = format !== "mdy"
  withOrder(format, () => {
    for (const l of lines)
      for (let i = 0; i < l.words.length; i++) {
        const d = dateAt(l.words, i)
        if (!d) continue
        dates.add(d.date)
        if (!proven && !withOrder("dmy", () => dateAt(l.words, i))) proven = true
      }
  })
  if (!proven) return miss
  const paise = (n: number | null) => (n == null ? null : Math.round(n * 100))
  const out: Row[] = []
  for (const r of rows) {
    const d = paise(r.debit) ?? 0
    const c = paise(r.credit) ?? 0
    const bal = card ? null : paise(r.balance)
    if (d < 0 || c < 0 || d > 0 === c > 0) return miss // one side, and only one, moves money
    if (!printed.has(d || c)) return miss
    if (!card && (bal === null || !printed.has(Math.abs(bal)))) return miss
    if (parseStatementDate(r.date, "ymd") !== r.date || !dates.has(r.date)) return miss
    out.push({
      date: r.date,
      cents: d || c,
      type: d ? "debit" : "credit",
      balance: bal,
      text: [r.description],
      ref: [],
      valueDate: null,
    })
  }
  if (!out.length) return { ok: false, reason: "no-table" }
  return check(
    out,
    lines.filter((l) => !looksLikeRow(l)),
    [],
    card,
  )
}

/** The most text the server's statement read takes (apps/api routes/statement-read.ts MAX_TEXT). */
const AI_MAX_TEXT = 200_000

/**
 * The statement as lines of cells with their x-ranges, for an AI read: "p1 y702 | 30-62 Date | 95-140
 * Narration". A statement too long for the server with the coordinates goes without them (rows only; the
 * read then names no layout).
 */
export function layoutText(items: readonly PdfItem[]): string {
  const lines = linesOf(wordsOf(items))
  const full = lines
    .map(
      (l) =>
        `p${l.page} y${Math.round(l.y)} | ${cellsOf(l.words)
          .map((c) => `${Math.round(c.x)}-${Math.round(c.x2)} ${c.s}`)
          .join(" | ")}`,
    )
    .join("\n")
  if (full.length <= AI_MAX_TEXT) return full
  return lines
    .map((l) =>
      cellsOf(l.words)
        .map((c) => c.s)
        .join(" | "),
    )
    .join("\n")
    .slice(0, AI_MAX_TEXT) // still over the limit: cut, so the read fails the balance check, not the server's 400
}
